import { ArrowLeftRight, Copy, MonitorSmartphone, Plus } from "lucide-react";
import { useState } from "react";

import {
  Button,
  Card,
  CardHeader,
  Dialog,
  EmptyState,
  Pill,
  SelectField,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TextField,
} from "@/components/ui";
import { formatRelativeTime } from "@/lib/format";
import { ApiError } from "@/api/client";
import {
  settingsRepository,
  type Device,
  type Till,
} from "@/repositories/settingsRepository";
import { useAuthStore } from "@/stores/authStore";

import {
  settingsKeys,
  useBranches,
  useDevices,
  useInvalidate,
  useMutation,
  useTills,
} from "../useSettingsData";

const STATUS_TONE = {
  ACTIVE: "success",
  PENDING: "warn",
  SUSPENDED: "danger",
  RETIRED: "neutral",
} as const;

/**
 * Tills and the terminals paired to them.
 *
 * This is where a manager registers a device and gets the pairing code the
 * cashier types into the till — the other half of the flow the login screen
 * points at.
 */
export function DevicesTab() {
  const canManage = useAuthStore((s) => s.can("devices.manage"));
  const devices = useDevices();
  const tills = useTills();

  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ code: string; device: string } | null>(
    null,
  );

  if (devices.isLoading || tills.isLoading) return <SkeletonRows rows={5} />;

  return (
    <div className="space-y-5">
      {issued && (
        <PairingCodeBanner
          code={issued.code}
          device={issued.device}
          onDismiss={() => setIssued(null)}
        />
      )}

      <Card flush className="p-5">
        <CardHeader
          title="Terminals"
          subtitle="Devices registered to this business."
          action={
            canManage && (
              <Button
                variant="primary"
                icon={<Plus className="size-4" aria-hidden />}
                onClick={() => setAdding(true)}
              >
                Register a terminal
              </Button>
            )
          }
        />

        {adding && (
          <RegisterDeviceForm
            onCancel={() => setAdding(false)}
            onRegistered={(code, device) => {
              setAdding(false);
              setIssued({ code, device });
            }}
          />
        )}

        {devices.data && devices.data.length > 0 ? (
          <DeviceTable
            devices={devices.data}
            canManage={canManage}
            onCode={setIssued}
          />
        ) : (
          !adding && (
            <EmptyState
              icon={MonitorSmartphone}
              title="No terminals registered"
              description="Register a till terminal to let cashiers sign in with a PIN."
            />
          )
        )}
      </Card>

      <Card flush className="p-5">
        <CardHeader title="Tills" subtitle="Points of sale, one per counter." />
        {tills.data && tills.data.length > 0 ? (
          <Table className="mt-3">
            <THead>
              <TH>Till</TH>
              <TH hideBelow="sm">Code</TH>
              <TH>Section</TH>
              <TH hideBelow="lg">Receipt prefix</TH>
            </THead>
            <TBody>
              {tills.data.map((till) => (
                <TR key={till.id}>
                  <TD className="font-medium">{till.name}</TD>
                  <TD hideBelow="sm" className="font-numeric">
                    {till.code}
                  </TD>
                  <TD className="text-ink-600">{till.section_name}</TD>
                  <TD hideBelow="lg" className="font-numeric text-ink-600">
                    {till.receipt_prefix}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        ) : (
          <EmptyState
            title="No tills yet"
            description="A till is created per counter."
          />
        )}
      </Card>
    </div>
  );
}

function DeviceTable({
  devices,
  canManage,
  onCode,
}: {
  devices: Device[];
  canManage: boolean;
  onCode: (v: { code: string; device: string }) => void;
}) {
  const invalidate = useInvalidate(settingsKeys.devices);

  const reissue = useMutation({
    mutationFn: (id: string) => settingsRepository.reissuePairingCode(id),
  });
  const suspend = useMutation({
    mutationFn: (id: string) => settingsRepository.suspendDevice(id),
    onSuccess: invalidate,
  });

  const [moving, setMoving] = useState<Device | null>(null);

  return (
    <Table className="mt-3">
      <THead>
        <TH>Terminal</TH>
        <TH hideBelow="sm">Till</TH>
        <TH>Status</TH>
        <TH hideBelow="lg">Last seen</TH>
        <TH hideBelow="md">Queued</TH>
        {canManage && <TH className="text-right">Actions</TH>}
      </THead>
      <TBody>
        {devices.map((device) => (
          <TR key={device.id}>
            <TD className="font-medium">{device.name}</TD>
            <TD hideBelow="sm" className="text-ink-600">
              {device.till_name ?? "—"}
            </TD>
            <TD>
              <Pill tone={STATUS_TONE[device.status]}>
                {device.status.toLowerCase()}
              </Pill>
            </TD>
            <TD hideBelow="lg" className="text-ink-600">
              {formatRelativeTime(device.last_seen_at)}
            </TD>
            <TD hideBelow="md">
              {/* Outbox depth. A growing queue means takings are not reaching
                  the server, which is worth noticing before anyone complains. */}
              <span
                className={
                  device.pending_operation_count > 0
                    ? "font-semibold text-warn-700"
                    : "text-ink-400"
                }
              >
                {device.pending_operation_count || "—"}
              </span>
            </TD>
            {canManage && (
              <TD className="text-right">
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    loading={
                      reissue.isPending && reissue.variables === device.id
                    }
                    onClick={() =>
                      reissue.mutate(device.id, {
                        onSuccess: (result) => {
                          invalidate();
                          onCode({
                            code: result.pairing_code,
                            device: device.name,
                          });
                        },
                      })
                    }
                  >
                    Pairing code
                  </Button>
                  <Button size="sm" onClick={() => setMoving(device)}>
                    <ArrowLeftRight className="size-4" aria-hidden /> Move
                  </Button>
                  {device.status === "ACTIVE" && (
                    <Button
                      size="sm"
                      variant="danger"
                      loading={
                        suspend.isPending && suspend.variables === device.id
                      }
                      onClick={() => suspend.mutate(device.id)}
                    >
                      Suspend
                    </Button>
                  )}
                </div>
              </TD>
            )}
          </TR>
        ))}
      </TBody>
      {moving && (
        <MoveDeviceDialog device={moving} onClose={() => setMoving(null)} />
      )}
    </Table>
  );
}

/**
 * Move a terminal to another counter.
 *
 * Phrased as a till rather than a section, because a till is what a manager can
 * point at: the section follows from it. Only tills at the terminal's own branch
 * are offered — the API refuses the rest, and offering a choice that will be
 * rejected is a worse way to say the same thing.
 */
function MoveDeviceDialog({
  device,
  onClose,
}: {
  device: Device;
  onClose: () => void;
}) {
  const tills = useTills();
  const invalidate = useInvalidate(settingsKeys.devices);
  const [till, setTill] = useState(device.default_till ?? "");

  const move = useMutation({
    mutationFn: (next: string) =>
      settingsRepository.moveDevice(device.id, {
        default_till: next || null,
        expected_version: device.version,
      }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
  });

  const options = (tills.data ?? []).filter(
    (t: Till) => t.branch === device.branch,
  );
  const error = move.error instanceof ApiError ? move.error : null;

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Move ${device.name}`}
      description="A terminal takes its section from its till, so moving it between counters means giving it a different one. It stays paired."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={move.isPending}
            disabled={(till || null) === device.default_till}
            onClick={() => move.mutate(till)}
          >
            Move terminal
          </Button>
        </>
      }
    >
      <SelectField
        label="Till"
        hint="Only tills at this terminal's branch."
        value={till}
        onChange={(e) => setTill(e.target.value)}
        options={[
          { value: "", label: "Not assigned" },
          ...options.map((t) => ({
            value: t.id,
            label: `${t.name} — ${t.section_name}`,
          })),
        ]}
      />
      {error && (
        <p className="mt-3 text-sm text-danger-700" role="alert">
          {/* A colleague moved the same terminal while this dialog was open. The
              version we hold is stale, so the list has to be re-read rather than
              the request retried. */}
          {error.code === "VERSION_CONFLICT"
            ? "Someone else changed this terminal. Close this and try again."
            : (error.detail ?? "Could not move the terminal.")}
        </p>
      )}
    </Dialog>
  );
}

function RegisterDeviceForm({
  onCancel,
  onRegistered,
}: {
  onCancel: () => void;
  onRegistered: (code: string, device: string) => void;
}) {
  const branches = useBranches();
  const tills = useTills();
  const invalidate = useInvalidate(settingsKeys.devices);

  const [name, setName] = useState("");
  const [branch, setBranch] = useState("");
  const [till, setTill] = useState("");

  const register = useMutation({
    mutationFn: settingsRepository.registerDevice,
    onSuccess: (device) => {
      invalidate();
      onRegistered(device.pairing_code, device.name);
    },
  });

  const branchId = branch || branches.data?.[0]?.id || "";
  const tillsForBranch = (tills.data ?? []).filter(
    (t) => t.branch === branchId,
  );

  return (
    <div className="mt-4 space-y-4 rounded-card bg-canvas p-4">
      <div className="grid gap-4 sm:grid-cols-3">
        <TextField
          label="Terminal name"
          placeholder="Butchery terminal"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <SelectField
          label="Branch"
          value={branchId}
          onChange={(e) => {
            setBranch(e.target.value);
            setTill("");
          }}
          options={(branches.data ?? []).map((b) => ({
            value: b.id,
            label: b.name,
          }))}
        />
        <SelectField
          label="Till"
          hint="Which counter this terminal normally works."
          value={till}
          onChange={(e) => setTill(e.target.value)}
          options={[
            { value: "", label: "Not assigned" },
            ...tillsForBranch.map((t) => ({
              value: t.id,
              label: `${t.name} (${t.code})`,
            })),
          ]}
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          loading={register.isPending}
          disabled={!name.trim() || !branchId}
          onClick={() =>
            register.mutate({
              name: name.trim(),
              branch: branchId,
              default_till: till || null,
            })
          }
        >
          Register
        </Button>
        <Button onClick={onCancel}>Cancel</Button>
        {register.isError && (
          <span className="text-sm text-danger-700" role="alert">
            Could not register the terminal.
          </span>
        )}
      </div>
    </div>
  );
}

function PairingCodeBanner({
  code,
  device,
  onDismiss,
}: {
  code: string;
  device: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Card className="border-brand-500/30 bg-brand-50" role="status">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink-900">
            Pairing code for {device}
          </p>
          {/* Shown once, on purpose: the code is never returned by the device
              list, so a manager must read it out or write it down now. */}
          <p className="mt-0.5 text-xs text-ink-600">
            Type this on the terminal&rsquo;s sign-in screen. It works once and
            expires in 15 minutes — this is the only time it is shown.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <span className="font-numeric text-3xl font-bold tracking-[0.3em] text-ink-900">
            {code}
          </span>
          <Button
            size="sm"
            icon={<Copy className="size-4" aria-hidden />}
            onClick={() => {
              void navigator.clipboard?.writeText(code);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            Dismiss
          </Button>
        </div>
      </div>
    </Card>
  );
}
